import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Alert, Box, Skeleton, Snackbar, TextField, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Countdown from './Countdown';
import CopyButton from './CopyButton';

const CopyToClipboard = (props) => {
  const { code, label = null, counter = null, sx } = props;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [value, setValue] = useState(code);
  const [isCopied, setIsCopied] = useState(false);
  const minutes = Math.floor(counter / 60);
  const seconds = counter % 60;
  const theme = useTheme();
  const tooltipMessage = isCopied ? `Copié: ${value}` : "Copier le code d'association dans le presse-papiers";

  useEffect(() => {
    setValue(code);
  }, [code]);

  const handleClickCopy = async () => {
    if ('clipboard' in navigator) {
      navigator.clipboard.writeText(code);
    } else {
      document.execCommand('copy', true, code);
    }
    setIsCopied(true);
    setOpenSnackbar(true);
  };

  const handleChange = (event) => {
    setValue(event.target.value);
    setIsCopied(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  const getCountdownColor = () => {
    if (counter < 30) {
      return theme.palette.error.main;
    }
    if (counter < 60) {
      return theme.palette.warning.main;
    }
  };

  const getHelperText = () => {
    if (!counter) {
      return 'Ce code a expiré';
    }
    if (counter < 60) {
      return `Expire dans ${seconds} secondes`;
    }
    return `Expire dans ${minutes} minutes ${seconds || ''}`;
  };

  const inputStartObj = <Countdown counter={counter} colors={theme.palette} getColor={getCountdownColor} />;
  const inputEndObj = <CopyButton isCopied={isCopied} value={value} handleClick={handleClickCopy} />;

  const styleTextField = {
    '.MuiFormHelperText-root': {
      color: getCountdownColor() || 'text.disabled'
    }
  };

  if (!code) {
    return <Skeleton height={80} width="100%" />;
  }

  return (
    <>
      <Box sx={sx} onClick={handleClickCopy}>
        <Tooltip title={tooltipMessage} arrow placement="top">
          <TextField
            autoComplete="off"
            disabled
            label={label}
            value={value}
            fullWidth
            slotProps={{ input: { startAdornment: inputStartObj, endAdornment: inputEndObj } }}
            helperText={getHelperText()}
            sx={styleTextField}
            onChange={handleChange}
          />
        </Tooltip>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={6000}
        open={openSnackbar}
        severity="success"
        sx={{ top: 'env(safe-area-inset-top) !important' }}
        onClose={handleCloseSnackbar}
      >
        <Alert elevation={6} severity="success" sx={{ width: '100%' }} onClose={handleCloseSnackbar}>
          Code copié dans le presse-papier
        </Alert>
      </Snackbar>
    </>
  );
};
CopyToClipboard.propTypes = {
  code: PropTypes.string,
  counter: PropTypes.number,
  label: PropTypes.string,
  sx: PropTypes.object
};

export default CopyToClipboard;
